import { useLayoutEffect, useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const scrollPositions = {};

export function useScrollRestoration() {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();
  const isRestoringRef = useRef(false);

  // Save scroll position continuously (floor to avoid sub-pixel issues)
  useEffect(() => {
    const handleScroll = () => {
      if (isRestoringRef.current) return;
      scrollPositions[key] = Math.floor(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [key]);

  // Restore scroll position on POP, scroll to top on PUSH/REPLACE
  useLayoutEffect(() => {
    if (navType !== "POP") {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }

    const saved = scrollPositions[key];
    if (saved === undefined) return;

    isRestoringRef.current = true;

    // Try immediately
    window.scrollTo({ top: saved, behavior: "instant" });

    // Poll + observe DOM mutations until page is tall enough
    let done = false;
    let attempts = 0;
    const maxAttempts = 30;

    const finish = () => {
      if (done) return;
      done = true;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: Math.min(saved, maxScroll), behavior: "instant" });
      requestAnimationFrame(() => {
        isRestoringRef.current = false;
      });
      observer.disconnect();
    };

    const tryRestore = () => {
      if (done) return;
      const h = document.documentElement.scrollHeight;
      const maxScroll = h - window.innerHeight;
      // Tolerance of 2px for sub-pixel rounding
      if (maxScroll >= saved - 2) {
        finish();
      } else if (++attempts >= maxAttempts) {
        finish();
      } else {
        setTimeout(tryRestore, 50);
      }
    };

    const observer = new MutationObserver(() => {
      if (done) return;
      const h = document.documentElement.scrollHeight;
      const maxScroll = h - window.innerHeight;
      if (maxScroll >= saved - 2) {
        finish();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    requestAnimationFrame(() => requestAnimationFrame(tryRestore));

    return () => {
      done = true;
      observer.disconnect();
    };
  }, [key, navType, pathname]);
}
